import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { narrativeService } from '../../services/api';

const NarrativesStep = ({ project, scenarios, drivers, factors, onDataChange, scenariosAmbNarrativa = [] }) => {
  const { t } = useTranslation();
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [newNarrative, setNewNarrative] = useState({
    initial_stage: '',
    intermediate_stage: '',
    final_stage: ''
  });

  const selectedScenarios = scenarios.filter(s => s.is_selected);

  useEffect(() => {
    if (selectedScenario) {
      fetchNarrative(selectedScenario.id);
    }
  }, [selectedScenario]);

  useEffect(() => {
    if (selectedScenarios.length > 0 && !selectedScenario) {
      setSelectedScenario(selectedScenarios[0]);
    }
  }, [selectedScenarios, selectedScenario]);

  // Versió de la narrativa que tenies quan vas començar a escriure.
  const [versioBase, setVersioBase] = useState(null);

  const fetchNarrative = async (scenarioId) => {
    try {
      const data = await narrativeService.getScenarioNarratives(scenarioId);
      // Since there's only one narrative per scenario, get the first one or null
      const narrative = data.length > 0 ? data[0] : null;
      
      if (narrative) {
        // Es guarda la versió de partida per poder detectar si algú altre
        // n'ha desat una de nova mentre tu escrivies.
        setVersioBase(narrative.version ?? null);
        setNewNarrative({
          initial_stage: narrative.initial_stage,
          intermediate_stage: narrative.intermediate_stage,
          final_stage: narrative.final_stage
        });
      } else {
        setVersioBase(null);
        setNewNarrative({
          initial_stage: '',
          intermediate_stage: '',
          final_stage: ''
        });
      }
    } catch (error) {
      console.error('Error fetching narrative:', error);
    }
  };

  const handleSaveNarrative = async () => {
    if (!selectedScenario) return;
    if (!newNarrative.initial_stage || !newNarrative.intermediate_stage || !newNarrative.final_stage) {
      toast.error(t('narratives.fillAllStages'));
      return;
    }

    setLoading(true);
    try {
      // Check if narrative already exists for this scenario
      const existingNarratives = await narrativeService.getScenarioNarratives(selectedScenario.id);
      
      if (existingNarratives.length > 0) {
        // Update existing narrative
        await narrativeService.updateNarrative(existingNarratives[0].id, {
          ...newNarrative,
          base_version: versioBase
        });
      } else {
        // Create new narrative
        await narrativeService.createNarrative({
          scenario_id: selectedScenario.id,
          ...newNarrative
        });
      }
      
      fetchNarrative(selectedScenario.id);
      // Avisa l'assistent perquè recalculi quins escenaris ja tenen narrativa:
      // d'això en depenen el distintiu de la llista, el comptador de la passa i
      // el botó d'acabar.
      if (onDataChange) onDataChange();
    } catch (error) {
      // 409: algú altre ha desat una versió nova mentre tu escrivies. El seu
      // text no s'ha perdut i el teu tampoc —el tens a la pantalla—, però no es
      // pot desar a sobre sense dir-ho.
      if (error.response?.status === 409) {
        toast.error(t('narratives.conflict'), { autoClose: false });
      } else {
        toast.error(t('narratives.saveError'));
      }
      console.error('Error saving narrative:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScenarioName = (scenario) => {
    return scenario.name || t('narratives.defaultScenarioName', { id: scenario.id });
  };

  const getScenarioDriverValues = (scenario) => {
    if (!scenario.driver_values) return [];
    
    return Object.entries(scenario.driver_values).map(([driverId, value]) => {
      const driver = drivers.find(d => d.id.toString() === driverId);
      return {
        driverName: driver?.causal_factor_name || t('narratives.unknownDriver'),
        value: value,
        isHigh: value === driver?.extreme_value_high
      };
    });
  };

  // Aquí hi havia `getScenarioIntensity`, que comptava quants drivers estaven
  // «alts» i en deia «Impacte alt / mitjà / baix». No el cridava ningú des de
  // feia temps, i el concepte tampoc s'aguanta: els extrems no són magnituds.

  const getScenarioSecondaryFactors = (scenario) => {
    if (!factors || !scenario.driver_values) return { constant: [], following: [] };
    
    const scenarioDriverIds = Object.keys(scenario.driver_values).map(id => parseInt(id));
    const secondaryFactors = factors.filter(f => !f.is_driver);
    
    const constant = [];
    const following = [];
    
    secondaryFactors.forEach(factor => {
      if (factor.secondary_factor_assignment) {
        const assignment = factor.secondary_factor_assignment;
        if (assignment.factor_type === 'constant') {
          constant.push(factor);
        } else if (assignment.factor_type === 'follows_driver') {
          // Check if this factor follows any drivers in this scenario
          const relevantToScenario = assignment.driver_ids?.some(driverId => 
            scenarioDriverIds.includes(driverId)
          );
          if (relevantToScenario) {
            following.push({
              ...factor,
              followsDrivers: assignment.driver_ids?.map(driverId => 
                drivers.find(d => d.id === driverId)?.causal_factor_name
              ).filter(Boolean) || []
            });
          }
        }
      }
    });
    
    return { constant, following };
  };

  const getTranslatedCategory = (category) => {
    const categoryMap = {
      'political': t('factors.pestel.political'),
      'economic': t('factors.pestel.economic'),
      'social': t('factors.pestel.social'),
      'technological': t('factors.pestel.technological'),
      'environmental': t('factors.pestel.environmental'),
      'legal': t('factors.pestel.legal'),
      'strengths': t('factors.swot.strengths'),
      'weaknesses': t('factors.swot.weaknesses'),
      'opportunities': t('factors.swot.opportunities'),
      'threats': t('factors.swot.threats'),
      'identify': t('factors.cyber.identify'),
      'protect': t('factors.cyber.protect'),
      'detect': t('factors.cyber.detect'),
      'respond': t('factors.cyber.respond'),
      'recover': t('factors.cyber.recover')
    };
    
    return categoryMap[category] || category;
  };

  if (selectedScenarios.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          {t('narratives.noScenariosSelected')}
        </h3>
        <p className="text-neutral-600">
          {t('narratives.selectScenariosFirstMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">{t('narratives.title')}</h3>
            <button
              onClick={() => setShowHelpModal(true)}
              className="w-5 h-5 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center text-neutral-500 hover:text-neutral-700 transition-colors"
              title={t('narratives.showHelp')}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-600">
              {t('narratives.scenariosStatus', { count: selectedScenarios.length })}
            </span>
            {project.time_horizon && (
              <div className="flex items-center gap-1 text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {project.time_horizon}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scenario Selection */}
      <div className="bg-white rounded-lg border border-neutral-200">
        <div className="p-4 border-b border-neutral-200">
          <h3 className="text-lg font-medium text-neutral-900 mb-3">{t('narratives.selectScenario')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {selectedScenarios.map(scenario => {
              const isActive = selectedScenario?.id === scenario.id;
              return (
                <button
                  key={scenario.id}
                  onClick={() => setSelectedScenario(scenario)}
                  className={`p-3 text-left rounded-lg border-2 transition-all ${
                    isActive 
                      ? 'border-accent-500 bg-accent-50' 
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                  }`}
                >
                  <div className="text-sm font-medium text-neutral-900">
                    {getScenarioName(scenario)}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {/* Abans això mirava `s.narrative`, un camp que
                        ScenarioResponse no retorna mai: la condició era sempre
                        falsa i el distintiu deia «encara no hi ha narrativa»
                        encara que l'acabessis de desar. Ara ve de la llista que
                        l'assistent ja calcula demanant les narratives de cada
                        escenari triat. */}
                    {scenariosAmbNarrativa.includes(scenario.id)
                      ? t('narratives.hasNarrative')
                      : t('narratives.noNarrativeYet')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Scenario Context */}
        {selectedScenario && (
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Driver Values */}
              <div>
                <h4 className="text-sm font-medium text-neutral-700 mb-3">{t('narratives.driverValues')}</h4>
                <div className="space-y-2">
                  {getScenarioDriverValues(selectedScenario).map((driverValue, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                      <span className="text-sm text-neutral-600">{driverValue.driverName}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-neutral-900">{driverValue.value}</span>
                        <span className="inline-flex items-center rounded border border-neutral-300 px-1.5 py-0.5 font-mono text-[10px] font-bold text-neutral-500">
                          {driverValue.isHigh ? t('narratives.high') : t('narratives.low')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Factors */}
              <div>
                <h4 className="text-sm font-medium text-neutral-700 mb-3">{t('narratives.relevantSecondaryFactors')}</h4>
                <div className="space-y-3">
                  {(() => {
                    const secondaryFactors = getScenarioSecondaryFactors(selectedScenario);
                    return (
                      <>
                        {secondaryFactors.constant.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">
                              {t('narratives.constantFactors')}
                            </h5>
                            <div className="space-y-1">
                              {secondaryFactors.constant.map(factor => (
                                <div key={factor.id} className="flex items-center justify-between text-sm">
                                  <span className="text-neutral-600">{factor.name}</span>
                                  <span className="text-neutral-400">{getTranslatedCategory(factor.category)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {secondaryFactors.following.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">
                              {t('narratives.followingDrivers')}
                            </h5>
                            <div className="space-y-1">
                              {secondaryFactors.following.map(factor => (
                                <div key={factor.id} className="text-sm">
                                  <span className="text-neutral-600">{factor.name}</span>
                                  <div className="text-xs text-neutral-400">
                                    → {factor.followsDrivers.join(', ')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {secondaryFactors.constant.length === 0 && secondaryFactors.following.length === 0 && (
                          <p className="text-sm text-neutral-400 italic">{t('narratives.noSecondaryFactors')}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Narrative Writing Panel */}
      {selectedScenario && (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div className="p-6 border-b border-neutral-200">
            <h3 className="text-lg font-medium text-neutral-900">
              {t('narratives.writingFor', { scenario: getScenarioName(selectedScenario) })}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {t('narratives.considerDriversAndFactors')}
            </p>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Initial Stage */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {t('narratives.initialStage')}
              </label>
              <textarea
                value={newNarrative.initial_stage}
                onChange={(e) => setNewNarrative(prev => ({ ...prev, initial_stage: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                placeholder={t('narratives.initialStagePlaceholder')}
              />
            </div>

            {/* Intermediate Stage */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {t('narratives.intermediateStage')}
              </label>
              <textarea
                value={newNarrative.intermediate_stage}
                onChange={(e) => setNewNarrative(prev => ({ ...prev, intermediate_stage: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                placeholder={t('narratives.intermediateStagePlaceholder')}
              />
            </div>

            {/* Final Stage */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {t('narratives.finalStage')}
              </label>
              <textarea
                value={newNarrative.final_stage}
                onChange={(e) => setNewNarrative(prev => ({ ...prev, final_stage: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                placeholder={t('narratives.finalStagePlaceholder')}
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveNarrative}
                disabled={loading || !newNarrative.initial_stage || !newNarrative.intermediate_stage || !newNarrative.final_stage}
                className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {loading ? t('narratives.saving') : t('narratives.saveNarrative')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-neutral-900">
                  {t('narratives.helpTitle')}
                </h3>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="prose prose-sm max-w-none text-neutral-700">
                <p className="mb-4">
                  {t('narratives.stepIntroduction')}
                </p>
                
                <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('narratives.whatToConsider')}:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm text-neutral-600">{t('narratives.considerDrivers')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm text-neutral-600">{t('narratives.considerConstantFactors')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-sm text-neutral-600">{t('narratives.considerFollowingFactors')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-neutral-900 mb-3">{t('narratives.narrativeStructure')}:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{t('narratives.initialStage')}</p>
                        <p className="text-sm text-neutral-600">{t('narratives.initialStageDesc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{t('narratives.intermediateStage')}</p>
                        <p className="text-sm text-neutral-600">{t('narratives.intermediateStageDesc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{t('narratives.finalStage')}</p>
                        <p className="text-sm text-neutral-600">{t('narratives.finalStageDesc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NarrativesStep;